import { Currency } from "@redotech/money/currencies";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import {
  CHECKOUT_IMAGE_BLOCK_MAX_WIDTH_PX,
  CheckoutImageBlockJustification,
  CheckoutOptimizationWidgetType,
  lowProfileManualProductSourceSchema,
  PurchaseCheckoutBlockRenderTarget,
  ShippingOptionRenderTarget,
  ThresholdManualProductSourceSchema,
} from "./widget";

const baseWidgetSchema = z.object({
  /**
   * Defined `id` means the widget is existing and needs to be updated.
   * Undefined `id` means the widget is new and needs to be created
   */
  id: zExt.objectId().optional(),
  name: z.string(),
  /**
   * @deprecated Widget existence in collection indicates it's enabled
   */
  enabled: z.boolean().optional(),
});

const baseStandardWidgetSchema = baseWidgetSchema.extend({
  /**
   * When true, this widget applies to all checkouts globally (not tied to a specific checkout experience)
   */
  enabledGlobally: z.boolean().optional(),
  target: createEnum(PurchaseCheckoutBlockRenderTarget),
});

export const checkoutWidgetsSchema = z.object({
  [CheckoutOptimizationWidgetType.THRESHOLD_UPSELL]: baseStandardWidgetSchema
    .extend({
      type: z.literal(CheckoutOptimizationWidgetType.THRESHOLD_UPSELL),
      thresholdAmount: z.number(),
      currency: createEnum(Currency),
      gteThresholdText: z.string(),
      ltThresholdText: z.string(),
      addToCartButtonColor: z.string(),
      addToCartButtonTextColor: z.string(),
      progressBarColor: z.string(),
      excludedProductTags: z.array(z.string()),
      fallbackProductTags: z.array(z.string()),
      manualProductSources: z.array(ThresholdManualProductSourceSchema),
    })
    .optional(),
  [CheckoutOptimizationWidgetType.LOW_PROFILE_PRODUCT_UPSELL]:
    baseStandardWidgetSchema
      .extend({
        type: z.literal(
          CheckoutOptimizationWidgetType.LOW_PROFILE_PRODUCT_UPSELL,
        ),
        addToCartButtonColor: z.string(),
        addToCartButtonTextColor: z.string(),
        excludedProductTags: z.array(z.string()),
        fallbackProductTags: z.array(z.string()),
        manualProductSources: z.array(lowProfileManualProductSourceSchema),
      })
      .optional(),
  [CheckoutOptimizationWidgetType.FULFILLMENT_CUTOFF]: baseStandardWidgetSchema
    .extend({
      type: z.literal(CheckoutOptimizationWidgetType.FULFILLMENT_CUTOFF),
      text: z.string(),
      backgroundColor: z.string(),
      iconColor: z.string(),
    })
    .optional(),
  [CheckoutOptimizationWidgetType.CONTENT_BLOCK]: baseStandardWidgetSchema
    .extend({
      type: z.literal(CheckoutOptimizationWidgetType.CONTENT_BLOCK),
      sections: z.array(
        z.object({
          _id: z.string(),
          title: z.string().nullish(),
          description: z.string().nullish(),
          imageUrl: z.string().nullish(),
        }),
      ),
    })
    .optional(),
  [CheckoutOptimizationWidgetType.IMAGE_BLOCK]: baseStandardWidgetSchema
    .extend({
      type: z.literal(CheckoutOptimizationWidgetType.IMAGE_BLOCK),
      imageUrl: z.string().nullish(),
      imageWidthPx: z.number().int().max(CHECKOUT_IMAGE_BLOCK_MAX_WIDTH_PX),
      imageJustification: createEnum(CheckoutImageBlockJustification),
    })
    .optional(),
  [CheckoutOptimizationWidgetType.SHIPPING_OPTION_CONTENT]: baseWidgetSchema
    .extend({
      target: createEnum(ShippingOptionRenderTarget),
      type: z.literal(CheckoutOptimizationWidgetType.SHIPPING_OPTION_CONTENT),
      title: z.string(),
      description: z.string(),
      imageUrl: z.string().nullish(),
      backgroundColor: z.string(),
    })
    .optional(),
});

/**
 * The global state store for the checkout widgets builder.
 */
const checkoutWidgetsBuilderStateSchema = z.object({
  selectedWidget: createEnum(CheckoutOptimizationWidgetType),
  checkoutWidgets: checkoutWidgetsSchema,
});
export type CheckoutWidgetsBuilderState = z.infer<
  typeof checkoutWidgetsBuilderStateSchema
>;
